import { createSelector } from '@reduxjs/toolkit';

import type { RecordOfSportEntity } from 'app/lib/betting/betting-entities/reducers/sports/types';
import type { TournamentEntityNative } from 'app/lib/betting/betting-entities/reducers/tournaments/types';
import type { RecordOfUnionEntity } from 'app/lib/betting/betting-entities/reducers/unions/types';

import type { BettingEntitiesSelectors } from 'app/lib/betting/betting-entities';
import { orderByGlobalPriority } from 'app/lib/grid';
import { sortByPriority } from 'app/lib/sortByPriorityImmutableData';

export type TournamentWithHierarchy = Omit<TournamentEntityNative, 'matchIds'> & {
  union: RecordOfUnionEntity;
  sport: RecordOfSportEntity;
  matchIds: Array<number>;
};

export const selectAllSportsGrid = (
  sports: BettingEntitiesSelectors['sports'],
  tournamentUnions: BettingEntitiesSelectors['tournamentUnions'],
  tournaments: BettingEntitiesSelectors['tournaments'],
  matches: BettingEntitiesSelectors['matches'],
) => {
  return createSelector(
    sports,
    tournamentUnions,
    tournaments,
    matches,
    (sports, tournamentUnions, tournaments, matches) => {
      const sortedUnions= sortByPriority(tournamentUnions);
      const sortedTournaments = sortByPriority(tournaments);

      const sortedSportsResult = sports.reduce((result, sport) => {
        const tournamentsWithHierarchy = new Map<number, TournamentWithHierarchy>();

        const sportUnions = sortedUnions.filter(({ sportId }) => (
          sportId === sport.id
        ));

        sportUnions.forEach((union) => {
          const unionTournaments = sortedTournaments.filter(({ tournamentUnionId }) => (
            tournamentUnionId === union.id
          ));

          unionTournaments.forEach((recordOfTournamentEntity) => {
            const tournament = recordOfTournamentEntity.toJS() as TournamentEntityNative;
            const tournamentWithHierarchy = {
              ...tournament,
              union,
              sport,
              matchIds: orderByGlobalPriority(matches, Array.from(tournament.matchIds)),
            };

            tournamentsWithHierarchy.set(
              tournament.id,
              tournamentWithHierarchy,
            );
          });
        });

        return result.set(sport.id, tournamentsWithHierarchy);
      }, new Map<number, Map<number, TournamentWithHierarchy>>());

      return sortedSportsResult;
    },
  );
};
